import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Clock, Mail, MapPin, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/i18n/LanguageContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSettings } from "@/lib/hooks";
import PageHeader from "@/components/PageHeader";

export default function Contact() {
  const { t } = useLang();
  const { data: settings } = useSettings();
  const location = useLocation();
  const prefillAddress = location.state?.address || "";

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: prefillAddress ? `${t("hero.coverageTitle")}: ${prefillAddress}` : "",
  });
  const [sending, setSending] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("contact.error"));
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "h-11 w-full rounded-lg border border-zinc-300 bg-white px-4 text-sm text-zinc-950 placeholder:text-zinc-400 focus:border-[#0D9488] focus:outline-none focus:ring-2 focus:ring-[#0D9488]/20 transition-colors";

  const infoItems = [
    { icon: MapPin, label: t("contact.addressLabel"), value: settings?.address, testId: "contact-address" },
    { icon: Phone, label: t("contact.phoneLabel"), value: settings?.phone, testId: "contact-phone" },
    { icon: Mail, label: t("contact.emailLabel"), value: (settings?.emails || []).join(" | "), testId: "contact-emails" },
    { icon: Clock, label: t("contact.hoursLabel"), value: t("contact.hours"), testId: "contact-hours" },
  ];

  return (
    <div data-testid="contact-page">
      <PageHeader title={t("contact.title")} subtitle={t("contact.subtitle")} testId="contact-title" />
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          {/* FORM */}
          <div className="lg:col-span-7">
            <h2 className="font-heading text-2xl font-bold tracking-tight text-zinc-950">{t("contact.formTitle")}</h2>
            <form onSubmit={handleSubmit} className="mt-7 space-y-5" data-testid="contact-form">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-zinc-700">{t("contact.name")} *</label>
                  <input data-testid="contact-input-name" required value={form.name} onChange={update("name")} className={inputCls} />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-zinc-700">{t("contact.email")} *</label>
                  <input data-testid="contact-input-email" required type="email" value={form.email} onChange={update("email")} className={inputCls} />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-zinc-700">{t("contact.phone")}</label>
                  <input data-testid="contact-input-phone" value={form.phone} onChange={update("phone")} className={inputCls} />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-zinc-700">{t("contact.subject")}</label>
                  <input data-testid="contact-input-subject" value={form.subject} onChange={update("subject")} className={inputCls} />
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-zinc-700">{t("contact.message")} *</label>
                <textarea
                  data-testid="contact-input-message"
                  required
                  rows={6}
                  value={form.message}
                  onChange={update("message")}
                  className="w-full rounded-lg border border-zinc-300 bg-white px-4 py-3 text-sm text-zinc-950 placeholder:text-zinc-400 transition-colors focus:border-[#0D9488] focus:outline-none focus:ring-2 focus:ring-[#0D9488]/20"
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="inline-flex items-center gap-2 rounded-lg bg-[#0D9488] px-8 py-3.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-[#0F766E] disabled:opacity-60"
              >
                <Send className="h-4 w-4" />
                {sending ? t("contact.sending") : t("contact.send")}
              </button>
            </form>
          </div>

          {/* INFO */}
          <div className="lg:col-span-5">
            <div className="rounded-2xl bg-[#062B2B] p-9">
              <h2 className="font-heading text-xl font-bold text-white">{t("contact.infoTitle")}</h2>
              <ul className="mt-7 space-y-6">
                {infoItems.map((item) => (
                  <li key={item.label} className="flex items-start gap-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#0D9488]/15 text-[#5EEAD4]">
                      <item.icon className="h-5 w-5" />
                    </span>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">{item.label}</p>
                      <p className="mt-1 text-sm leading-relaxed text-zinc-300" data-testid={item.testId}>{item.value}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-6 overflow-hidden rounded-2xl border border-zinc-200">
              <iframe
                title="ÇağrıNET Map"
                data-testid="contact-map"
                src="https://www.openstreetmap.org/export/embed.html?bbox=28.7483%2C36.6881%2C28.7883%2C36.7281&layer=mapnik&marker=36.7081%2C28.7683"
                className="h-64 w-full border-0"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
